import React, { useEffect, useMemo, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import piAPI from "../../../api/inventory/piApi";
import { PI_STATUSES } from "../../../constants/inventoryConstants";
import SupplierSelect from "../../../components/partials/inventory/common/SupplierSelect";
import LocationSelect from "../../../components/partials/inventory/common/LocationSelect";
import ProductSKUSearch from "../../../components/partials/inventory/common/ProductSKUSearch";
import { useParams } from "react-router-dom";
import RecordPaymentModal from "../../../components/partials/inventory/tabs/RecordPaymentModal";

function AddNew() {
  const { id } = useParams();
  const piId = id;

  const isEditMode = !!piId;

  const INITIAL_FORM_DATA = {
    supplierId: "",
    locationId: "",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    referenceNumber: "",
    items: [],
    discount: 0,
    shipping: 0,
    notes: "",
    terms: "",
    action: "receive",
  };

  // Receive mode (only in edit when status is draft/pending/partial)
  const [receiveMode, setReceiveMode] = useState(false);
  const [receiveQuantities, setReceiveQuantities] = useState({}); // { itemId: qty }

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // State
  const [existingPI, setExistingPI] = useState(null);

  /***** state change  *****/
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ====================================================
  // Load existing PI if in edit mode
  // ====================================================
  useEffect(() => {
    fetchData();
  }, [piId, isEditMode]);

  const fetchData = async () => {
    if (!isEditMode) return;
    setLoading(true);

    try {
      const res = await piAPI.get(piId);

      if (res.code === 200) {
        const pi = res.data;

        setExistingPI(pi);
        setFormData({
          supplierId: pi.supplierId,
          locationId: pi.locationId,
          invoiceNumber: pi.invoiceNumber,
          invoiceData: pi.invoiceDate?.slice(0, 10),
          dueDate: pi.dueDate?.slice(0, 10),
          referenceNumber: pi.referenceNumber,
          discount: pi.discount || 0,
          shipping: pi.shipping || 0,
          notes: pi.notes || "",
          terms: pi.terms || "",
          items: (pi.items || []).map((item) => ({
            id: item.id,
            inventoryItemId: item.inventoryItemId,
            sku: item.sku,
            productName: item.productName || "Product",
            variantName: item.variantName || "",
            quantity: Number(item.quantity) || 0,
            quantityReceived: Number(item.quantityReceived) || 0,
            unitCost: parseFloat(item.unitCost) || 0,
            taxRate: Number(item.taxRate) || 0,
            discountPercent: Number(item.discountPercent) || 0,
          })),
        });
      }
    } catch (err) {
      showError("Failed to load invoice");
    } finally {
    }
  };

  // ====================================================
  // Item management
  // ====================================================
  const excludeIds = useMemo(
    () => formData.items.map((i) => i.inventoryItemId),
    [formData.items],
  );

  const addItem = (product) => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          inventoryItemId: product.inventoryItemId,
          sku: product.sku,
          productName: product.productName,
          variantName: product.variantName,
          quantity: 1,
          quantityReceived: 0,
          unitCost: product.lastCost ? Number(product.lastCost) : 0,
          taxRate: formData.supplier?.defaultTaxRate || 18,
          discountPercent: 0,
        },
      ],
    }));
  };

  const updateLine = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((it, i) =>
        i === idx
          ? {
              ...it,
              [field]: value,
            }
          : it,
      ),
    }));
  };

  const removeLine = (idx) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  // ====================================================
  // Calculations
  // ====================================================
  const totals = useMemo(() => {
    let subtotal = 0;
    let totalDiscount = 0;
    let taxAmount = 0;

    formData.items.forEach((i) => {
      const qty = parseFloat(i.quantity) || 0;
      const cost = parseFloat(i.unitCost) || 0;
      const taxRate = parseFloat(i.taxRate) || 0;
      const discountPct = parseFloat(i.discountPercent) || 0;

      const lineSub = qty * cost;
      const lineDiscount = lineSub * (discountPct / 100);
      const lineTax = (lineSub - lineDiscount) * (taxRate / 100);

      subtotal += lineSub;
      totalDiscount += lineDiscount;
      taxAmount += lineTax;
    });

    const overallDiscount = parseFloat(formData.discount) || 0;
    const overallShipping = parseFloat(formData.shipping) || 0;
    const grandTotal =
      subtotal - totalDiscount + taxAmount - overallDiscount + overallShipping;

    return {
      subtotal,
      totalDiscount,
      taxAmount,
      overallDiscount,
      overallShipping,
      grandTotal,
    };
  }, [formData.items, formData.discount, formData.shipping]);

  // ====================================================
  // Validation
  // ====================================================
  const validate = () => {
    const errs = {};
    if (!formData.supplierId) errs.supplier = "Supplier required";
    if (!formData.locationId) errs.location = "Location required";
    if (!formData.invoiceNumber.trim())
      errs.invoiceNumber = "Invoice number required";
    if (!formData.invoiceDate) errs.invoiceDate = "Invoice date required";
    if (formData.items.length === 0) errs.items = "Add at least one item";
    formData.items.forEach((it, idx) => {
      const qty = parseInt(it.quantity, 10);
      const cost = parseFloat(it.unitCost);
      if (!qty || qty <= 0) errs[`item_${idx}_qty`] = "Invalid quantity";
      if (cost < 0 || isNaN(cost)) errs[`item_${idx}_cost`] = "Invalid cost";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ====================================================
  // Save handlers
  // ====================================================
  const buildPayload = (action) => ({
    supplierId: formData.supplierId,
    locationId: formData.locationId,
    invoiceNumber: formData.invoiceNumber.trim(),
    invoiceDate: formData.invoiceDate,
    dueDate: formData.dueDate || null,
    referenceNumber: formData.referenceNumber.trim() || null,
    items: formData.items.map((i) => ({
      inventoryItemId: i.inventoryItemId,
      productName: i.productName,
      variantName: i.variantName,
      quantity: parseInt(i.quantity, 10),
      unitCost: parseFloat(i.unitCost),
      taxRate: parseFloat(i.taxRate || 0),
      discountPercent: parseFloat(i.discountPercent || 0),
    })),
    discount: parseFloat(formData.discount) || 0,
    shipping: parseFloat(formData.shipping) || 0,
    notes: formData.notes,
    terms: formData.terms,
    action: action,
  });

  const handleSave = async (action) => {
    if (!validate()) {
      showError("Please fix validation errors");
      return;
    }

    setLoading(true);

    try {
      const res = await piAPI.create(buildPayload(action));

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to save invoice";
      const code = err.response?.data?.code;
      if (code === "DUPLICATE_RESOURCE") {
        setErrors({ invoiceNumber: "This invoice number already exists" });
      }
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // Receive handlers (edit mode for draft/partial PI)
  // ====================================================
  const handleStartReceive = () => {
    const initial = {};
    formData.items.forEach((item) => {
      const remaining = item.quantity - item.quantityReceived;
      if (remaining > 0) initial[item.id] = remaining;
    });
    setReceiveQuantities(initial);
    setReceiveMode(true);
  };

  const handleConfirmReceive = async () => {
    const receiveItems = Object.entries(receiveQuantities)
      .filter(([_, qty]) => parseInt(qty, 10) > 0)
      .map(([itemId, qty]) => ({
        purchaseInvoiceItemId: itemId,
        quantityToReceive: parseInt(qty, 10),
      }));

    if (receiveItems.length === 0) {
      showError("Enter quantities to receive");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        items: receiveItems,
        notes: formData.notes,
      };

      const res = await piAPI.receive(piId, payload);

      if (res.code === 200) {
        showSuccess(`Stock received. Invoice status: ${res.status}`);
        setReceiveMode(false);
        fetchData();
      }
    } catch (err) {
      showError(err.response?.data?.error || "Failed to receive");
    } finally {
      setLoading(false);
    }
  };

  // Reload PI from server and sync local state
  const reloadPI = async () => {
    const updated = await piAPI.get(piId);
    setExistingPI(updated.data);
    // setItems(
    //   updated.data.items.map((item) => ({
    //     id: item.id,
    //     inventoryItemId: item.inventoryItemId,
    //     sku: item.sku,
    //     productName: item.productName || "Product",
    //     variantName: item.variantName || "",
    //     quantity: item.quantity,
    //     quantityReceived: item.quantityReceived,
    //     unitCost: parseFloat(item.unitCost || 0),
    //     taxRate: item.taxRate || 0,
    //     discountPercent: item.discountPercent || 0,
    //   })),
    // );
    return updated.data;
  };

  const handlePaymentSuccess = async () => {
    setPaymentModalOpen(false);
    await reloadPI();
  };

  // ====================================================
  // Render guards
  // ====================================================

  const isReadOnly =
    isEditMode &&
    existingPI &&
    ["received", "cancelled"].includes(existingPI.status);

  const canReceive =
    isEditMode &&
    existingPI &&
    ["draft", "pending", "partial"].includes(existingPI.status);

  const statusConfig = existingPI ? PI_STATUSES[existingPI.status] : null;

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>
                {isEditMode
                  ? `Invoice ${formData.invoiceNumber}`
                  : "New Purchase Invoice"}
                {statusConfig && (
                  <span
                    className={`badge bg-${statusConfig.color}-subtle text-${statusConfig.color} ms-2`}
                  >
                    {statusConfig.label}
                  </span>
                )}
              </h4>
              {existingPI ? (
                <p className="text-muted small mb-0">
                  Created{" "}
                  {new Date(existingPI.createdAt).toLocaleDateString("en-IN")}
                  {existingPI.receivedAt &&
                    ` · Received ${new Date(existingPI.receivedAt).toLocaleDateString("en-IN")}`}
                </p>
              ) : (
                <p>Add Details to generate purchase invoice </p>
              )}
            </div>
          </div>
          <div className="page-btn">
            {!isEditMode && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={loading}
                  onClick={() => handleSave("draft")}
                >
                  <i className="ti ti-device-floppy me-1" /> Save Draft
                </button>
                <button
                  type="button"
                  className="btn btn-primary ms-2"
                  disabled={loading}
                  onClick={() => handleSave("receive")}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-check me-1" /> Save & Receive
                    </>
                  )}
                </button>
              </>
            )}

            {canReceive && !receiveMode && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStartReceive}
              >
                <i className="ti ti-package-import me-1" /> Receive Items
              </button>
            )}

            {receiveMode && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setReceiveMode(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success ms-2"
                  onClick={handleConfirmReceive}
                >
                  Confirm Receive
                </button>
              </>
            )}
          </div>
        </div>

        <div className="row">
          <div className="col-lg-8">
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h4>Supplier & Location</h4>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-7">
                    <label className="form-label small">
                      Supplier <span className="text-danger">*</span>
                    </label>
                    {isReadOnly || isEditMode ? (
                      <div className="form-control bg-light">
                        {existingPI?.supplierName || "—"}
                      </div>
                    ) : (
                      <>
                        <SupplierSelect
                          name="supplierId"
                          value={formData.supplierId}
                          onChange={handleChange}
                        />
                        {errors.supplier && (
                          <div className="text-danger small mt-1">
                            {errors.supplier}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small">
                      Receive at Location <span className="text-danger">*</span>
                    </label>
                    {isReadOnly || isEditMode ? (
                      <div className="form-control bg-light">
                        {existingPI?.locationName || "—"}
                      </div>
                    ) : (
                      <>
                        <LocationSelect
                          name="locationId"
                          value={formData.locationId}
                          onChange={handleChange}
                          required
                        />
                        {errors.location && (
                          <div className="text-danger small mt-1">
                            {errors.location}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Info */}
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h4>Invoice Details</h4>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small">
                      Invoice Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.invoiceNumber ? "is-invalid" : ""}`}
                      value={formData.invoiceNumber}
                      name="invoiceNumber"
                      onChange={handleChange}
                      placeholder="e.g. INV-12345"
                      disabled={isEditMode}
                    />
                    {errors.invoiceNumber && (
                      <div className="invalid-feedback">
                        {errors.invoiceNumber}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">
                      Invoice Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.invoiceDate}
                      name="invoiceDate"
                      onChange={handleChange}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">Due Date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dueDate"
                      value={formData.dueDate || ""}
                      onChange={handleChange}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small">
                      Reference / PO Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="referenceNumber"
                      value={formData.referenceNumber || ""}
                      onChange={handleChange}
                      placeholder="Optional reference"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h4>Line Items</h4>
                {formData.items.length > 0 && (
                  <small className="text-muted">
                    {formData.items.length} items
                  </small>
                )}
              </div>
              <div className="card-body">
                {!isReadOnly && !receiveMode && (
                  <div className="mb-3">
                    <ProductSKUSearch
                      onSelect={addItem}
                      excludeIds={excludeIds}
                      locationId={formData.locationId}
                      placeholder="Search SKU, barcode, or product name to add..."
                    />
                  </div>
                )}

                {formData.items.length === 0 ? (
                  <div className="text-center text-muted py-4 border rounded">
                    <i className="ti ti-package fs-1 d-block mb-2 opacity-50" />
                    No items added yet
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Item</th>
                          <th style={{ width: 80 }}>Qty</th>
                          {(isEditMode || receiveMode) && (
                            <th style={{ width: 90 }}>Received</th>
                          )}
                          {receiveMode && (
                            <th style={{ width: 100 }}>Receive Now</th>
                          )}
                          <th style={{ width: 110 }}>Unit Cost</th>
                          <th style={{ width: 80 }}>Tax %</th>
                          <th style={{ width: 80 }}>Disc %</th>
                          <th style={{ width: 110 }}>Line Total</th>
                          {!isReadOnly && !receiveMode && (
                            <th style={{ width: 40 }} />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((line, idx) => {
                          const qty = parseFloat(line.quantity) || 0;
                          const cost = parseFloat(line.unitCost) || 0;
                          const taxRate = parseFloat(line.taxRate) || 0;
                          const discPct = parseFloat(line.discountPercent) || 0;
                          const lineSub = qty * cost;
                          const lineDisc = lineSub * (discPct / 100);
                          const lineTax =
                            (lineSub - lineDisc) * (taxRate / 100);
                          const lineTotal = lineSub - lineDisc + lineTax;
                          const remaining =
                            line.quantity - line.quantityReceived;

                          return (
                            <tr key={line.id || line.inventoryItemId}>
                              <td>
                                <div className="fw-semibold small">
                                  {line.productName}
                                </div>
                                <div className="text-muted small">
                                  {line.variantName && `${line.variantName} · `}
                                  <code>{line.sku}</code>
                                </div>
                              </td>

                              <td>
                                {isReadOnly || receiveMode ? (
                                  <span className="small">{line.quantity}</span>
                                ) : (
                                  <input
                                    type="number"
                                    min="1"
                                    className={`form-control form-control-sm ${errors[`item_${idx}_qty`] ? "is-invalid" : ""}`}
                                    value={line.quantity}
                                    onChange={(e) =>
                                      updateLine(
                                        idx,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </td>

                              {(isEditMode || receiveMode) && (
                                <td>
                                  <span
                                    className={`small ${remaining > 0 ? "text-warning" : "text-success"}`}
                                  >
                                    {line.quantityReceived} / {line.quantity}
                                  </span>
                                </td>
                              )}

                              {receiveMode && (
                                <td>
                                  {remaining > 0 ? (
                                    <input
                                      type="number"
                                      min="0"
                                      max={remaining}
                                      className="form-control form-control-sm"
                                      value={receiveQuantities[line.id] || ""}
                                      onChange={(e) =>
                                        setReceiveQuantities((prev) => ({
                                          ...prev,
                                          [line.id]: e.target.value,
                                        }))
                                      }
                                    />
                                  ) : (
                                    <span className="text-success small">
                                      <i className="ti ti-check" /> Done
                                    </span>
                                  )}
                                </td>
                              )}
                              <td>
                                {isReadOnly || receiveMode ? (
                                  <span className="small">
                                    ₹{cost.toFixed(2)}
                                  </span>
                                ) : (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    value={line.unitCost}
                                    onChange={(e) =>
                                      updateLine(
                                        idx,
                                        "unitCost",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </td>
                              <td>
                                {isReadOnly || receiveMode ? (
                                  <span className="small">{taxRate}%</span>
                                ) : (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    value={line.taxRate}
                                    onChange={(e) =>
                                      updateLine(idx, "taxRate", e.target.value)
                                    }
                                  />
                                )}
                              </td>
                              <td>
                                {isReadOnly || receiveMode ? (
                                  <span className="small">{discPct}%</span>
                                ) : (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="form-control form-control-sm"
                                    value={line.discountPercent}
                                    onChange={(e) =>
                                      updateLine(
                                        idx,
                                        "discountPercent",
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                              </td>
                              <td className="fw-semibold small">
                                ₹{lineTotal.toFixed(2)}
                              </td>
                              {!isReadOnly && !receiveMode && (
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link text-danger p-0"
                                    onClick={() => removeLine(idx)}
                                  >
                                    <i className="ti ti-x" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {errors.items && (
                  <div className="text-danger small mt-2">{errors.items}</div>
                )}
              </div>
            </div>

            {/* Notes & Terms */}
            <div className="card mb-3">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h4>Notes & Terms</h4>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small">Internal Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      placeholder="Internal notes (not shown on printed invoice)..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small">
                      Terms & Conditions
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      name="terms"
                      value={formData.terms}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      placeholder="Payment terms, etc..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — summary sidebar */}
          <div className="col-lg-4">
            <div className="card mb-3 sticky-top">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h4>Invoice Summary</h4>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">Subtotal:</span>
                  <strong>₹{totals.subtotal.toFixed(2)}</strong>
                </div>

                {totals.totalDiscount > 0 && (
                  <div className="d-flex justify-content-between mb-2 small">
                    <span className="text-muted">Line Discounts:</span>
                    <span className="text-danger">
                      −₹{totals.totalDiscount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className="d-flex justify-content-between mb-2 small">
                  <span className="text-muted">Tax:</span>
                  <strong>₹{totals.taxAmount.toFixed(2)}</strong>
                </div>

                <div className="d-flex justify-content-between mb-2 small align-items-center">
                  <span className="text-muted">Overall Discount:</span>
                  {isReadOnly || receiveMode ? (
                    <span>₹{Number(formData.discount).toFixed(2)}</span>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm text-end"
                      style={{ width: 100 }}
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                    />
                  )}
                </div>

                <div className="d-flex justify-content-between mb-3 small align-items-center">
                  <span className="text-muted">Shipping:</span>
                  {isReadOnly || receiveMode ? (
                    <span>₹{Number(formData.shipping).toFixed(2)}</span>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="form-control form-control-sm text-end"
                      style={{ width: 100 }}
                      name="shipping"
                      value={formData.shipping}
                      onChange={handleChange}
                    />
                  )}
                </div>

                <hr />

                <div className="d-flex justify-content-between fw-bold">
                  <span>Grand Total:</span>
                  <span>₹{totals.grandTotal.toFixed(2)}</span>
                </div>

                {existingPI && (
                  <>
                    <hr />
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="text-muted">Payment Status:</span>
                      <span
                        className={`badge bg-${existingPI.paymentStatus === "paid" ? "success" : "warning"}-subtle text-${existingPI.paymentStatus === "paid" ? "success" : "warning"}`}
                      >
                        {existingPI.paymentStatus}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Amount Paid:</span>
                      <span>₹{Number(existingPI.amountPaid).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {existingPI && existingPI.status !== "cancelled" && (
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title mb-3 small text-uppercase text-muted">
                    Actions
                  </h6>

                  {/* Record Payment — available unless fully paid */}
                  {existingPI.paymentStatus !== "paid" && (
                    <button
                      className="btn btn-primary btn-sm w-100 mb-2"
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      <i className="ti ti-cash me-1" /> Record Payment
                    </button>
                  )}

                  {existingPI.paymentStatus === "paid" && (
                    <div className="alert alert-success py-2 small text-center mb-2">
                      <i className="ti ti-circle-check me-1" /> Fully Paid
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <RecordPaymentModal
        isOpen={paymentModalOpen}
        pi={existingPI}
        onClose={() => setPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
      />
    </>
  );
}

export default AddNew;
